<template>
  <div class="battle pb-10">
    <p class="battle__title">
      Battle
      <span class="battle__title--description">(optional)</span>
    </p>
    <div class="battle__wrap-blocks d-flex flex-wrap flex-column flex-sm-row justify-center align-center">
      <div
        v-for="battle in eventData.battles"
        :key="battle.id"
        class="battle__block d-flex flex-column justify-center align-center"
      >
        <v-btn
          class="battle__block-edit"
          color="var(--color-blue-800)"
          icon
          text
          @click="handlerClickEdit(battle.id)"
        >
          <v-icon>
            {{ mdiLeadPencil }}
          </v-icon>
        </v-btn>
        <v-btn
          class="battle__block-del"
          color="var(--color-blue-800)"
          icon
          text
          @click="confirmDeleteBattle(battle.id)"
        >
          <v-icon>
            {{ mdiTrashCanOutline }}
          </v-icon>
        </v-btn>
        <span class="battle__block-title">{{ battle.title }}</span>
        <p class="battle__block-subtitle">
          {{ getBattleCategoryName(battle.crewSize) }}
        </p>
      </div>

      <BKConfirmationDialog
        :confirm-text="confirmationDialog.confirmText"
        :is-show="confirmationDialog.isShow"
        :title="confirmationDialog.title"
        @cancel="confirmationDialog.isShow = false"
        @confirm="confirmationDialog.confirm"
      >
        {{ confirmationDialog.subtitle }}
      </BKConfirmationDialog>

      <div class="battle__block d-flex justify-center align-center">
        <v-btn
          :disabled="loadingAddBtn"
          :loading="loadingAddBtn"
          class="battle__btn-add"
          color="var(--color-blue-800)"
          outlined
          tile
          @click="addBattle"
        >
          Add Battle
          <v-icon class="ml-4">
            {{ mdiPlus }}
          </v-icon>
        </v-btn>
      </div>
    </div>
    <CreateEventAddBattle
      v-if="dialog"
      :battle-id="currentBattleId"
      :is-edit-mode="true"
      :is-show-modal.sync="dialog"
      @updateBattle="updateBattle"
    />
    <EventEditActionButtons
      :is-loading="false"
      :disabled="false"
      :error="errorMessage"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import api from '@/api'

import CreateEventAddBattle from '@/pages/CreateEventProcessingPage/ui/CreateEventAddBattle.vue'
import { BKConfirmationDialog } from '@/shared/ui/modals'
import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { mdiLeadPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { battleApi, BattleDB, useBattle } from '@/enitites/battle'
import { IEventEdit } from '@/enitites/event'
import { useQueryClient } from '@tanstack/vue-query'
import { eventKey } from '@/enitites/event/api/keys'
import { useSanitize } from '@/services/useSanitize'

const { getBattleCategoryName } = useBattle()
const queryClient = useQueryClient()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const props = defineProps<{
  eventData: IEventEdit
}>()

const dialog = ref(false)
const currentBattleId = ref(0)
const loadingAddBtn = ref(false)
const errorMessage = ref<string>()
const confirmationDialog = ref({
  isShow: false,
  title: '',
  subtitle: '',
  confirmText: '',
  confirm: () => {},
})

function handleNext() {
  emit('next')
}

function handleCancel() {
  emit('cancel')
}

async function deleteBattle(battleId: number) {
  const resp = await api.battles.deleteBattle(battleId)
  if (resp) {
    queryClient.invalidateQueries({
      queryKey: eventKey.edit(props.eventData.id),
    })
  }
}

function confirmDeleteBattle(id: number) {
  confirmationDialog.value = {
    isShow: true,
    title: 'Delete',
    subtitle: 'Are you sure you want to delete this battle',
    confirmText: 'Delete',
    confirm: () => {
      deleteBattle(id)
      confirmationDialog.value.isShow = false
    },
  }
}

async function addBattle() {
  loadingAddBtn.value = true
  const battle = await battleApi.addBattle(props.eventData.id)
  loadingAddBtn.value = false

  const eventBattleChip = {
    id: battle.id,
    title: battle.title,
    crewSize: battle.crewSize,
    isLimitExceeded: battle.isLimitExceeded,
    isRegistered: false,
    limit: battle.limit,
    orgChip: {
      id: 0,
      name: '',
      image: '',
    },
  }

  if (battle) {
    // Update event data without direct mutation
    const updatedBattles = [...props.eventData.battles, eventBattleChip]
    queryClient.setQueryData(eventKey.edit(props.eventData.id), (oldData: IEventEdit | undefined) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        battles: updatedBattles,
      }
    })
    currentBattleId.value = battle.id
    dialog.value = true
  }
}

const { sanitize } = useSanitize()

async function updateBattle(battle: BattleDB) {
  const mutatedBattle = { ...battle }
  if (mutatedBattle.description) {
    mutatedBattle.description = sanitize(battle.description)
  }
  const resp = await api.battles.update(mutatedBattle)
  if (resp) {
    const updatedBattles = props.eventData.battles.map((bat) =>
      bat.id === mutatedBattle.id ? { ...bat, title: mutatedBattle.title, crewSize: mutatedBattle.crewSize } : bat
    )
    queryClient.setQueryData(eventKey.edit(props.eventData.id), (oldData: IEventEdit | undefined) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        battles: updatedBattles,
      }
    })
  }
}

function handlerClickEdit(battleId: number) {
  currentBattleId.value = battleId
  dialog.value = true
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.hidden {
  visibility: hidden;
}

.show {
  visibility: visible;
}

.wrap {
  max-width: 400px;
  margin: 0 auto;
}

.battle {
  max-width: 1500px; // вмещается 4 блока батла
  min-height: 550px;
  margin: 0 auto;
  padding-top: 120px;

  &__block {
    position: relative;
    width: 100%;
    max-width: 360px;
    height: 230px;
    margin: 5px;
    padding: 0 45px;
    background: var(--color-grey-700);

    &-title {
      @include font-h2;

      color: var(--color-dark-800);
      font-weight: var(--font-weight-bold);
      text-align: center;
      word-break: break-all;
    }

    &-subtitle {
      @include font-h5;

      color: var(--color-dark-800);
      text-align: center;
    }

    &-edit {
      position: absolute;
      top: 15px;
      right: 15px;
    }

    &-del {
      position: absolute;
      top: 50px;
      right: 15px;
    }
  }

  &__title {
    display: block;
    width: 100%;
    max-width: 360px;
    margin: 12px auto;
    color: var(--color-dark-800);

    &--description {
      color: #999;
    }
  }

  &__btn-add {
    font-weight: bold;
    border: 2px solid;
  }
}
</style>
